import { ChangeEvent, useRef, useState } from 'react';

import { Button, Typography, makeStyles } from '@material-ui/core';

import type { eventWithTime } from 'rrweb';

import { Content, Header, Page } from '@backstage/core-components';

import { SessionReplayPlayer } from '@spotify/confidence-session-replay-react';
import { withPageImpression } from '@spotify/plugin-confidence-tracking-react';

const useStyles = makeStyles({
  content: {
    padding: 'var(--bui-space-6)',
  },
  upload: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--bui-space-4)',
    padding: 'var(--bui-space-12)',
    border: '2px dashed var(--bui-border-disabled)',
    borderRadius: 'var(--bui-radius-2)',
    textAlign: 'center',
  },
  loadAnotherButton: {
    marginBottom: 'var(--bui-space-4)',
  },
});

function SessionReplayPageContent() {
  const classes = useStyles();
  const [events, setEvents] = useState<eventWithTime[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        const eventArray = Array.isArray(parsed) ? parsed : parsed.events;
        if (!Array.isArray(eventArray)) {
          setError('File must contain a JSON array of rrweb events.');
          return;
        }
        setEvents(eventArray);
      } catch {
        setError('Failed to parse JSON file.');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file.');
    };
    reader.readAsText(file);
  };

  return (
    <Page themeId="tool">
      <Header title="Session replay" />
      <Content className={classes.content}>
        {events ? (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setEvents(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className={classes.loadAnotherButton}
            >
              Load another recording
            </Button>
            <SessionReplayPlayer events={events} />
          </>
        ) : (
          <div className={classes.upload}>
            <Typography variant="h6">Upload a session recording</Typography>
            <Typography variant="body2" color="textSecondary">
              Select a JSON file containing rrweb events
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </div>
        )}
      </Content>
    </Page>
  );
}

export const SessionReplayPage = withPageImpression(SessionReplayPageContent, {
  pageId: 'session-replay',
});
